import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { sanitizeUrl } from 'utils/sanitizeUrl';

export const reactPlugin = new ReactPlugin();

const appInsightsConfig = {
  config: {
    connectionString: import.meta.env.VITE_APP_APPINSIGHTS_CONNECTION_STRING || '',

    // Enable automatic tracking
    enableAutoRouteTracking: true,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,

    // W3C Distributed Tracing (for frontend-backend correlation)
    distributedTracingMode: 2, // DistributedTracingModes.W3C
    enableCorsCorrelation: true,
    correlationHeaderExcludedDomains: [],

    // Performance and reliability
    enableSessionStorageBuffer: true,
    maxBatchInterval: 15000, // Send telemetry every 15 seconds
    disableFetchTracking: false,
    enableUnhandledPromiseRejectionTracking: true,

    // GDPR Compliance - disable cookies if needed
    disableCookiesUsage: false,
    isCookieUseDisabled: false,
    cookieDomain: undefined,

    // Sampling - Reduce telemetry volume if needed
    samplingPercentage: 100,

    // Extensions
    extensions: [reactPlugin],

    // Custom telemetry initializers for GDPR and data sanitization
    extensionConfig: {
      [reactPlugin.identifier]: {
        history: undefined // Will be set during initialization with router history
      }
    }
  }
};

let appInsights: ApplicationInsights | null = null;
const connectionString = import.meta.env.VITE_APP_APPINSIGHTS_CONNECTION_STRING;

if (connectionString) {
  try {
    appInsights = new ApplicationInsights(appInsightsConfig);
    appInsights.loadAppInsights();

    // Add telemetry initializer for GDPR compliance and data sanitization
    appInsights.addTelemetryInitializer((envelope) => {
      // Sanitize URLs in the envelope baseData (main data payload)
      if (envelope.baseData) {
        const baseData = envelope.baseData as Record<string, any>;

        // Sanitize all URL-like fields (removed baseType check to ensure ALL dependencies are sanitized)
        if (baseData.data) {
          baseData.data = sanitizeUrl(baseData.data);
        }
        if (baseData.name) {
          baseData.name = sanitizeUrl(baseData.name);
        }
        if (baseData.target) {
          baseData.target = sanitizeUrl(baseData.target);
        }
        if (baseData.uri) {
          baseData.uri = sanitizeUrl(baseData.uri);
        }
        if (baseData.url) {
          baseData.url = sanitizeUrl(baseData.url);
        }
      }

      // Also sanitize URLs in envelope.data if present
      if (envelope.data) {
        const data = envelope.data as Record<string, any>;
        if (data.url) {
          data.url = sanitizeUrl(data.url);
        }
        if (data.uri) {
          data.uri = sanitizeUrl(data.uri);
        }
        if (data.target) {
          data.target = sanitizeUrl(data.target);
        }
      }

      // Sanitize URLs in custom properties (catches any URL-like strings in properties)
      if (envelope.data?.properties) {
        const properties = envelope.data.properties as Record<string, any>;
        Object.keys(properties).forEach((key) => {
          const value = properties[key];
          if (typeof value === 'string' && (value.includes('http://') || value.includes('https://'))) {
            properties[key] = sanitizeUrl(value);
          }
        });
      }

      // Add environment metadata to all telemetry
      envelope.data = envelope.data || {};
      envelope.data.environment = import.meta.env.MODE || 'development';
      envelope.data.appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

      return true;
    });

    console.info('Application Insights initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
    appInsights = null;
  }
} else {
  console.warn('Application Insights connection string not found. Telemetry will not be collected.');
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

export function isAppInsightsInitialized(): boolean {
  return appInsights !== null && !!connectionString;
}

export { appInsights, SeverityLevel };
